import type { ItemSnapshot } from "../../../api/types";
import { layoutMiniMap, miniMapWords } from "../model/miniMap";

/**
 * The small map of a gallery tile (DEC-1016-34, 36): the battery limit as a line around the item, its components
 * inside it with the ring of their state, tie-in marks on the line, Connections as solid stubs with a small box
 * and links as dashed stubs, and the colleagues' proposals as faint dashed shapes. It shows no ontology
 * content of its own: it draws what the snapshot says.
 */
export function ItemMiniMap({ name, snapshot }: { name: string; snapshot: ItemSnapshot }) {
  const layout = layoutMiniMap(snapshot);
  const words = miniMapWords(name, snapshot);
  const { limit } = layout;
  return (
    <svg className="nv-mini" viewBox={`0 0 ${layout.width} ${layout.height}`} role="img" aria-label={words} focusable="false">
      <title>{words}</title>
      <rect className="nv-mini__limit" x={limit.x} y={limit.y} width={limit.w} height={limit.h} rx={10} />
      {layout.components.map((c) => (
        <rect
          key={c.id}
          className="nv-mini__part"
          data-state={c.state ?? "none"}
          data-focus={c.inFocus ? undefined : "out"}
          x={c.x}
          y={c.y}
          width={c.w}
          height={c.h}
          rx={3}
        >
          <title>{c.name}</title>
        </rect>
      ))}
      {layout.faint.map((p) => (
        <rect key={p.cardId} className="nv-mini__faint" x={p.x} y={p.y} width={p.w} height={p.h} rx={3}>
          <title>{`${p.name} (proposed)`}</title>
        </rect>
      ))}
      {layout.tieIns.map((mark) => (
        <circle key={mark.componentId} className="nv-mini__tie" cx={mark.x} cy={mark.y} r={3.2} />
      ))}
      {layout.stubs.map((stub) => (
        <g key={`${stub.kind}:${stub.name}:${stub.y1}:${stub.x1}`} className="nv-mini__stub" data-kind={stub.kind} data-open={stub.open || undefined}>
          <line x1={stub.x1} y1={stub.y1} x2={stub.x2} y2={stub.y2} />
          {stub.kind === "connection" && <rect x={Math.min(stub.x1, stub.x2) + (Math.abs(stub.x2 - stub.x1) - 6) / 2} y={stub.y1 - 3} width={6} height={6} rx={1} />}
          <title>{stub.open ? `${stub.name} (open, own session)` : stub.name}</title>
        </g>
      ))}
    </svg>
  );
}
